import { useEffect, useState, useCallback } from "react";
import { Params, useParams, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";

import "./style.scss";
import { ReactComponent as TrashIcon } from "../../assets/trash.icon.svg";
import { ReactComponent as MoonIcon } from "../../assets/moon.icon.svg";
import { ReactComponent as MbaPlaceholder } from "../../assets/placeholder-mba.svg";
import LeftMenuLayout from "../../components/layout/left-menu-layout.comp";
import ChatList from "../../components/chat-list/chat-list.comp";
import CreateChatMessages from "../create-chat-messages/create-chat-messages";
import { ChatMessages } from "../chat-messages/chat-messages";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { ThemeContainer } from "../../common/containers/theme/theme.container";
import { User } from "../../common/interfaces/comps/user.interface";
import { Chat } from "../../common/interfaces/chats/chat.interface";
import { KnowledgeBase } from "../../common/interfaces/admin/knowledge-base.interface";
import { fetchRooms, archiveRoom, archiveAllRooms } from "../../api/chats/endpoints-referrers";
import { isMobileDevice } from "../../common/utilities/platform/platform.util";
import { DropdownMenuItem } from "../../components/dropdown-menu/dropdown-menu.comp";
import { ModalTemplate } from "../../components/modal-template/modal-template.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../components/confirmation-popup/confirmation-popup.comp";
import HeaderLayout from "../../components/layout/header-layout.comp";
import Footer from "../../components/footer/footer.comp";
import FeedbackView from "../admin/feedback/feedback";

const ChatBot = ({ isNewChat = false }: { isNewChat?: boolean }) => {
    const navigate = useNavigate();
    const { chatId } = useParams<Params<string>>();
    const {
        isTrial,
        isAdmin,
        user,
        knowledgeBase,
        knowledgeBases
    }: {
        isTrial: boolean;
        isAdmin?: boolean;
        user: User | null;
        knowledgeBase?: KnowledgeBase;
        knowledgeBases: KnowledgeBase[];
    } = FirebaseAuthContainer.useContainer();
    const { leftMenuCollapsed, isLightTheme, changeTheme } = ThemeContainer.useContainer();
    const [settings, setSettings] = useState<DropdownMenuItem[]>([]);
    const [chats, setChats] = useState<Chat[]>([]);
    const [settingChat, setSettingChat] = useState<Chat>();
    const [newChat, setNewChat] = useState<Chat | null>(null);
    const [newMessage, setNewMessage] = useState<string | null>(null);
    const [removeChatConfirmationOpen, setRemoveChatConfirmationOpen] = useState<boolean>(false);
    const [clearHistoryConfirmationOpen, setClearHistoryConfirmationOpen] =
        useState<boolean>(false);
    const [upgradeScreenOpen, setUpgradeScreenOpen] = useState<boolean>(false);

    const handleChangeTheme = useCallback(() => {
        changeTheme();
    }, [changeTheme]);

    useEffect(() => {
        setSettings([
            {
                icon: TrashIcon,
                title: "Clear chat history",
                handler: () => {
                    setClearHistoryConfirmationOpen(true);
                },
                mobileSupported: true
            },
            {
                icon: MoonIcon,
                title: isLightTheme ? "Dark theme" : "Light theme",
                handler: handleChangeTheme,
                mobileSupported: true
            }
        ]);
    }, [isLightTheme, handleChangeTheme]);

    useEffect(() => {
        if (newChat) {
            setChats(prevChats => [newChat, ...prevChats]);
        }
    }, [newChat]);

    useEffect(() => {
        if (user?.currentKnowledgeBaseId) {
            fetchRooms(user.uid, user.currentKnowledgeBaseId).then(response => {
                const {
                    data: { items }
                } = response;
                setChats(items);

                if (chatId && !items.find((item: Chat) => item.roomId === chatId)) {
                    navigate("/new-chat");
                }
            });
        } else {
            setNewChat(null);
            setNewMessage(null);
        }
    }, [user, chatId, navigate]);

    const handleChatCreating = () => {
        navigate("/new-chat");
    };

    const handleChatDeleting = () => {
        setRemoveChatConfirmationOpen(false);

        if (!user || !settingChat) return;

        archiveRoom(user.uid, settingChat.roomId).then(() => {
            setChats(prevChats => prevChats.filter(chat => chat.roomId !== settingChat.roomId));
            setSettingChat(undefined);
            if (chatId === settingChat.roomId) {
                handleChatCreating();
            }
        });
    };

    const handleDeleteChatConfirmationAction = (chat: Chat) => {
        setSettingChat(chat);
        setRemoveChatConfirmationOpen(true);
    };

    const handleClearHistoryConfirmationAction = () => {
        if (!user) return;

        archiveAllRooms(user.uid).then(() => {
            setChats([]);
            handleChatCreating();
        });
        setClearHistoryConfirmationOpen(false);
    };

    const handleUpgradeScreenOpen = () => {
        setUpgradeScreenOpen(true);
    };

    const handleUpgradeScreenClose = () => {
        setUpgradeScreenOpen(false);
    };

    return (
        <div className="chat-bot">
            <LeftMenuLayout
                leftMenu={
                    <ChatList
                        chats={chats}
                        settings={settings}
                        selectedChatId={chatId}
                        collapsedType={leftMenuCollapsed}
                        showKBList={knowledgeBases.length >= 1}
                        showAdminSettings={!!isAdmin}
                        handleChatDeleting={handleDeleteChatConfirmationAction}
                    />
                }
            >
                <HeaderLayout
                    footer={<Footer setNewMessage={setNewMessage} />}
                    subheaderHeight={60}
                    subheader={
                        isMobileDevice() && isTrial ? (
                            <div className="header-mobile-trial">
                                <span className="header-mobile-trial__text">
                                    {`Trial version expires in ${knowledgeBase?.expiringTrialPeriod} days`}
                                </span>
                                <Button
                                    className="trial-button header-mobile-trial__button"
                                    variant="text"
                                    onClick={handleUpgradeScreenOpen}
                                >
                                    Upgrade
                                </Button>
                            </div>
                        ) : undefined
                    }
                >
                    {isNewChat ? (
                        <CreateChatMessages
                            newMessage={newMessage}
                            setNewMessage={setNewMessage}
                            setNewChat={setNewChat}
                        />
                    ) : (
                        <ChatMessages
                            newMessage={newMessage}
                            setNewMessage={setNewMessage}
                            placeholder={MbaPlaceholder}
                        />
                    )}
                </HeaderLayout>
            </LeftMenuLayout>
            <ModalTemplate
                open={upgradeScreenOpen}
                width={654}
                handleClose={handleUpgradeScreenClose}
                ariaLabel="Upgrade to full version"
            >
                <FeedbackView handleClose={handleUpgradeScreenClose} />
            </ModalTemplate>
            <ConfirmationPopup
                open={clearHistoryConfirmationOpen}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Clear history"
                description="Are you sure you want to delet all your chats?"
                actionName="Delete"
                handleAction={handleClearHistoryConfirmationAction}
                handleClose={() => setClearHistoryConfirmationOpen(false)}
            />
            <ConfirmationPopup
                open={removeChatConfirmationOpen}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Delete"
                description={`Are you sure you want to delete "${settingChat?.title}"?`}
                actionName="Delete"
                handleAction={handleChatDeleting}
                handleClose={() => setRemoveChatConfirmationOpen(false)}
            />
        </div>
    );
};

export default ChatBot;
